import React, { useState } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView,
  Dimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import { useToast } from "../../../../components/LhToastContainer";
import { apiClient, createApiClient } from "../../../../api/apiClient";
import { RootState } from "../../../../store";

// Define __DEV__ if it doesn't exist (for web environments)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";

interface SpecificPeriodTouchPointActionProps {
  onComplete: () => boolean | Promise<boolean>; // Allow both sync and async validation
  onSaveForLater?: () => boolean | Promise<boolean> | void; // Allow async save for later
  onBackToDashboard?: () => void;
  onSuccess?: (message: string) => void; // Add callback for success messages
  selectedTouchPoint?: any;
  periodType?: "30" | "60" | "90" | "preferred_method"; // Add period type property
  isFormComplete?: boolean; // Add prop to indicate if form is complete
  isSubmitting?: boolean; // Add prop to indicate if form is being submitted
  isViewOnlyMode?: boolean; // Add prop to indicate if form is in view-only mode
  scrollRef?: React.RefObject<ScrollView>; // Add scroll ref for scrolling to top
}
// Detect iPad-like devices: iOS and width >= 768 (common breakpoint for iPad)
const { width: SCREEN_WIDTH } = Dimensions.get("window");
const isPad = Platform.OS === "ios" && SCREEN_WIDTH >= 768;
const SpecificPeriodTouchPointAction: React.FC<
  SpecificPeriodTouchPointActionProps
> = ({
  onComplete,
  onSaveForLater,
  onSuccess,
  periodType,
  isFormComplete = false,
  isSubmitting: externalIsSubmitting = false,
  isViewOnlyMode = false,
  scrollRef,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  // Toast hook
  const { showToast } = useToast();
  
  // Redux selectors
  const userEmail = useSelector(
    (state: RootState) => state.auth?.user?.username
  );
  const selectedEmployeeId = useSelector(
    (state: RootState) => state.nho?.selectedFilters?.employeeGpid
  );
  const probForm = useSelector(
    (state: RootState) => state.nho?.selectedFilters?.observationType
  );
  const location = useSelector(
    (state: RootState) => state.appData?.selectedLocation?.displayText
  );

  // Use external isSubmitting prop or internal state
  const actualIsSubmitting = externalIsSubmitting || isSubmitting;

  // State for touchpoint status - in a real app, this would come from Redux
  const [touchPointStatus, setTouchPointStatus] = useState<
    "new" | "in-progress" | "completed"
  >("new");

  // Get appropriate title based on period type
  const getPeriodTypeLabel = () => {
    switch (periodType) {
      case "30":
        return "30 Day";
      case "60":
        return "60 Day";
      case "90":
        return "90 Day";
      case "preferred_method":
        return "Preferred Method";
      default:
        return "Specific Period";
    }
  };

  // Function to handle PDF export via email
  const handlePdfExport = async () => {
    if (isPdfExportDisabled) {
      return;
    }

    // Validate required parameters
    if (!userEmail) {
      console.error("PDF Export Error: User email not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "User email not found. Please log in again.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!selectedEmployeeId) {
      console.error("PDF Export Error: Selected employee ID not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "Employee not selected. Please select an employee.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!probForm) {
      console.error("PDF Export Error: Observation type not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext:
          "Observation type not selected. Please select an observation type.",
        variant: "error",
        position: "top",
      });
      return;
    }

    if (!location) {
      console.error("PDF Export Error: Location not found");
      scrollRef?.current?.scrollTo({ y: 0, animated: true });
      showToast({
        header: "PDF Export Failed",
        subtext: "Location not selected. Please select a location.",
        variant: "error",
        position: "top",
      });
      return;
    }

    try {
      setIsPdfExporting(true);

      console.log(
        `Exporting ${getPeriodTypeLabel()} PDF for employee:`,
        selectedEmployeeId
      );
      console.log("PDF Export params:", {
        emailTo: userEmail,
        inspection_module: "nho_prob",
        id: selectedEmployeeId,
        prob_form: probForm,
        location: location,
      });

      // Create API client
      const api = createApiClient();

      // Build URL with query parameters
      const url = `/notifications/nho/email/pdf?emailTo=${encodeURIComponent(
        userEmail
      )}&inspection_module=nho_prob&id=${encodeURIComponent(
        selectedEmployeeId
      )}&prob_form=${encodeURIComponent(
        probForm
      )}&location=${encodeURIComponent(location)}`;

      // Make API call to export PDF
      const response = await apiClient(api, url, "GET");

      console.log("PDF Export API Response:", response);

      // Scroll to top and show success message
      scrollRef?.current?.scrollTo({ y: 0, animated: true });

      if (onSuccess) {
        onSuccess(
          `${getPeriodTypeLabel()} PDF export email sent successfully!`
        );
      } else {
        // Fallback to toast if no success callback provided
        showToast({
          header: "PDF Export Successful",
          subtext:
            response?.message ||
            `${getPeriodTypeLabel()} PDF export email sent successfully!`,
          variant: "success",
          position: "top",
        });
      }
    } catch (error: any) {
      console.error(`${getPeriodTypeLabel()} PDF Export Error:`, error);

      // Scroll to top for error display
      scrollRef?.current?.scrollTo({ y: 0, animated: true });

      // Handle different error types
      let errorMessage = `Failed to send ${getPeriodTypeLabel()} PDF export email`;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid request parameters";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast({
        header: "PDF Export Failed",
        subtext: errorMessage,
        variant: "error",
        position: "top",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  const handleSubmit = async (action: "save" | "complete") => {
    try {
      // Only manage internal submission state if not externally managed
      if (!externalIsSubmitting) {
        setIsSubmitting(true);
      }

      let isValid = false;

      if (action === "complete") {
        // For completion, call the validation function
        isValid = await Promise.resolve(onComplete());
      } else {
        // For save for later, call the save function if provided
        if (onSaveForLater) {
          const result = await Promise.resolve(onSaveForLater());
          // If onSaveForLater doesn't return a boolean, assume success
          isValid = typeof result === "boolean" ? result : true;
        } else {
          isValid = true; // No save function provided, just show message
        }
      }

      // If validation/submission failed, exit early
      if (!isValid) {
        if (!externalIsSubmitting) {
          setIsSubmitting(false);
        }
        return;
      }

      // Update touchpoint status based on action
      if (action === "complete") {
        setTouchPointStatus("completed");
        // Call success callback if provided, otherwise parent component will handle success/error messages and navigation
        if (onSuccess) {
          onSuccess(
            `${getPeriodTypeLabel()} Touchpoint completed successfully!`
          );
        }
      } else {
        // Save for later action
        setTouchPointStatus("in-progress");

        // Only show toast if onSaveForLater was not provided (fallback behavior)
        if (!onSaveForLater) {
          if (onSuccess) {
            onSuccess(`${getPeriodTypeLabel()} Touchpoint saved for later!`);
          } else {
            // Fallback to toast if no success callback provided
            showToast({
              header: "Saved for Later",
              subtext: `${getPeriodTypeLabel()} Touchpoint saved for later!`,
              variant: "info",
              position: "top",
            });
          }
        }
        // If onSaveForLater was provided, let the parent handle the success message
      }
    } catch (error) {
      console.error(`${getPeriodTypeLabel()} Submit error:`, error);
      showToast({
        header: "Error",
        subtext: "An error occurred. Please try again.",
        variant: "error",
        position: "top",
      });
    } finally {
      // Only manage internal submission state if not externally managed
      if (!externalIsSubmitting) {
        setIsSubmitting(false);
      }
    }
  };

  // Determine if the PDF export box should be disabled
  // In view-only mode, PDF should be enabled regardless of completion status
  const isPdfExportDisabled =
    !isViewOnlyMode && touchPointStatus !== "completed";

  return (
    <View style={styles.actionRow}>
      <View style={styles.buttonColumn}>
        {/* PDF Export Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            styles.pdfExportButton,
            isPdfExportDisabled ? styles.disabledButton : {},
            Platform.OS === "web" && !isPdfExportDisabled
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={isPdfExportDisabled ? () => {} : handlePdfExport}
          disabled={isPdfExportDisabled}
          accessibilityState={{ disabled: isPdfExportDisabled }}
          accessibilityLabel={
            isPdfExportDisabled
              ? `Export ${getPeriodTypeLabel()} to PDF (disabled until completed)`
              : isPdfExporting
              ? `Sending ${getPeriodTypeLabel()} PDF email`
              : `Export ${getPeriodTypeLabel()} to PDF`
          }
        >
          <View style={styles.buttonContent}>
            {isPdfExporting ? (
              <ActivityIndicator
                size="small"
                color="#034895"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="picture-as-pdf"
                size={20}
                color="#034895"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isPdfExportDisabled && styles.buttonTextDisabled,
              ]}
            >
              {isPdfExportDisabled
                ? "PDF Export Not Available"
                : isPdfExporting
                ? "Sending Email..."
                : "Export to PDF"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Save for Later Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            actualIsSubmitting ||
            touchPointStatus === "completed" ||
            isViewOnlyMode
              ? styles.disabledButton
              : styles.saveButton,
            Platform.OS === "web" &&
            !(
              actualIsSubmitting ||
              touchPointStatus === "completed" ||
              isViewOnlyMode
            )
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => handleSubmit("save")}
          disabled={
            actualIsSubmitting ||
            touchPointStatus === "completed" ||
            isViewOnlyMode
          }
          accessibilityState={{
            disabled:
              actualIsSubmitting ||
              touchPointStatus === "completed" ||
              isViewOnlyMode,
          }}
          accessibilityLabel={
            actualIsSubmitting
              ? "Save for later (disabled during submission)"
              : touchPointStatus === "completed"
              ? "Save for later (disabled for completed touchpoint)"
              : isViewOnlyMode
              ? "Save for later (disabled in view-only mode)"
              : "Save for later"
          }
        >
          <View style={styles.buttonContent}>
            {actualIsSubmitting ? (
              <ActivityIndicator
                size="small"
                color="#034895"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="save"
                size={20}
                color="#034895"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                (actualIsSubmitting || isViewOnlyMode) &&
                  styles.buttonTextDisabled,
              ]}
            >
              {actualIsSubmitting
                ? "Please wait..."
                : isViewOnlyMode
                ? "Saved"
                : "Save for later"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Complete Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            actualIsSubmitting ||
            touchPointStatus === "completed" ||
            !isFormComplete ||
            isViewOnlyMode
              ? styles.disabledButton
              : styles.completeButton,
            Platform.OS === "web" &&
            !(
              actualIsSubmitting ||
              touchPointStatus === "completed" ||
              !isFormComplete ||
              isViewOnlyMode
            )
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => handleSubmit("complete")}
          disabled={
            actualIsSubmitting ||
            touchPointStatus === "completed" ||
            !isFormComplete ||
            isViewOnlyMode
          }
          accessibilityState={{
            disabled:
              actualIsSubmitting ||
              touchPointStatus === "completed" ||
              !isFormComplete ||
              isViewOnlyMode,
          }}
          accessibilityLabel={
            actualIsSubmitting
              ? `Complete ${getPeriodTypeLabel()} form (disabled during submission)`
              : touchPointStatus === "completed"
              ? "Complete form (disabled for completed touchpoint)"
              : !isFormComplete
              ? "Complete form (disabled until all requirements are met)"
              : isViewOnlyMode
              ? "Complete form (disabled in view-only mode)"
              : `Complete ${getPeriodTypeLabel()} form`
          }
        >
          <View style={styles.buttonContent}>
            {actualIsSubmitting ? (
              <ActivityIndicator
                size="small"
                color="white"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="check-circle"
                size={20}
                color="white"
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText1,
                (actualIsSubmitting || !isFormComplete || isViewOnlyMode) &&
                  styles.buttonTextDisabled,
              ]}
            >
              {actualIsSubmitting
                ? "Submitting..."
                : !isFormComplete
                ? "Complete (Requirements not met)"
                : isViewOnlyMode
                ? "Completed"
                : "Complete"}
            </Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === "web" ? "row" : isPad ? "row" : "column",
    flexWrap: "wrap",
    //marginTop: 30,
    //borderTopWidth: 1,
    //borderTopColor: "#dee2e6",
    //paddingTop: 24,
    gap: 20,
    alignItems: "center",
    width: Platform.OS === "web" ? "60%" : isPad ? "100%" : "100%",
  },
  buttonColumn: {
    flex: 2,
    flexDirection: Platform.OS === "web" ? "row" : isPad ? "row" : "column", // Row for web, column for mobile
    gap: 10,
    minWidth: 0,
    width: "100%",
  },
  buttonFlex: {
    flex: 1,
    minWidth: 0, // Allow buttons to shrink if needed
  },
  customButton: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 60,
    backgroundColor: "#FFFFFF",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    justifyContent: "center",
  },
  saveButton: {
    backgroundColor: "white", // Orange for save
    borderWidth:1,
    borderColor:"#034895",
    borderRadius: 60,
  },
  completeButton: {
    backgroundColor: "#034895", // Blue for complete
    borderWidth:1,
    borderColor:"white", // White for complete
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: "#999",
    borderWidth: 1,
    borderColor: "#ccc",
    shadowOpacity: 0.1,
    borderRadius: 60,
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  pdfExportButton: {
    backgroundColor: "white", // Red for PDF export
    borderColor: "#034895",
    justifyContent: "center",
    borderRadius: 60,
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    marginRight: 8,
  },
  buttonText: {
    color: "#034895",
    fontSize: 16,
    fontWeight: "600",
  },
  buttonText1: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  buttonTextDisabled: {
    color: "#FFFFFF",
  },
});

export default SpecificPeriodTouchPointAction;
